/** @format */

import React, { useState, useEffect } from "react"
import "./AdminPage.css"

// Вкладки админки
const TABS = [
    { key: "bookings", label: "📋 Бронирования" },
    { key: "prices", label: "💰 Цены" },
    { key: "calendar", label: "📅 Календарь" },
]

const MONTHS = [
    "Январь",
    "Февраль",
    "Март",
    "Апрель",
    "Май",
    "Июнь",
    "Июль",
    "Август",
    "Сентябрь",
    "Октябрь",
    "Ноябрь",
    "Декабрь",
]
const DAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"]

const STATUS_LABELS = {
    PENDING: "Ожидает оплаты",
    PAID: "Оплачено",
    CONFIRMED: "Подтверждено",
    CANCELLED: "Отменено",
    MANUAL_BLOCK: "Ручная блокировка",
}

const STATUS_COLORS = {
    PENDING: "#f59e0b",
    PAID: "#2E7D4F",
    CONFIRMED: "#1B3A2D",
    CANCELLED: "#dc2626",
    MANUAL_BLOCK: "#f59e0b",
}

// Форматирование даты в "YYYY-MM-DD" из компонентов (без UTC-сдвига)
function toLocalDateStr(year, month, day) {
    const mm = String(month + 1).padStart(2, "0")
    const dd = String(day).padStart(2, "0")
    return `${year}-${mm}-${dd}`
}

// Сегодняшняя дата в формате "YYYY-MM-DD"
const TODAY = (() => {
    const now = new Date()
    return toLocalDateStr(now.getFullYear(), now.getMonth(), now.getDate())
})()

function nextDateStr(dateStr) {
    const date = new Date(`${dateStr}T12:00:00`)
    date.setDate(date.getDate() + 1)
    return toLocalDateStr(date.getFullYear(), date.getMonth(), date.getDate())
}

function sameManualBlockInfo(a, b) {
    return (
        (a.guest_name || "") === (b.guest_name || "") &&
        Number(a.guest_count || 0) === Number(b.guest_count || 0) &&
        (a.phone || "") === (b.phone || "") &&
        (a.comment || "") === (b.comment || "") &&
        (a.reason || "") === (b.reason || "")
    )
}

function createManualBlockItem(rows, groupKey) {
    const sortedRows = [...rows].sort((a, b) => a.date.localeCompare(b.date))
    const first = sortedRows[0]
    const last = sortedRows[sortedRows.length - 1]

    return {
        id: `manual-${groupKey}`,
        type: "manual_block",
        status: "MANUAL_BLOCK",
        guest_name: first.guest_name || "Ручная блокировка",
        guest_count: first.guest_count || 0,
        phone: first.phone || "",
        check_in: first.date,
        check_out: last.date,
        comment: first.comment || "",
        dates: sortedRows.map((row) => row.date),
        manual_block_id: first.manual_block_id || null,
        sort_date: first.date,
    }
}

function groupManualBlockedDates(blockedDates) {
    const manualDates = blockedDates
        .filter((date) => !date.booking_id && (date.source === "manual" || date.source == null))
        .sort((a, b) => a.date.localeCompare(b.date))

    const groupedById = new Map()
    const withoutGroupId = []

    manualDates.forEach((date) => {
        if (date.manual_block_id) {
            if (!groupedById.has(date.manual_block_id)) {
                groupedById.set(date.manual_block_id, [])
            }
            groupedById.get(date.manual_block_id).push(date)
        } else {
            withoutGroupId.push(date)
        }
    })

    const groups = Array.from(groupedById.entries()).map(([groupId, rows]) =>
        createManualBlockItem(rows, groupId),
    )

    let currentGroup = []
    withoutGroupId.forEach((date) => {
        const previous = currentGroup[currentGroup.length - 1]
        const isSameGroup = previous && nextDateStr(previous.date) === date.date && sameManualBlockInfo(previous, date)

        if (!isSameGroup && currentGroup.length > 0) {
            groups.push(createManualBlockItem(currentGroup, currentGroup.map((row) => row.id).join("-")))
            currentGroup = []
        }

        currentGroup.push(date)
    })

    if (currentGroup.length > 0) {
        groups.push(createManualBlockItem(currentGroup, currentGroup.map((row) => row.id).join("-")))
    }

    return groups
}

function AdminPage() {
    const [token, setToken] = useState(localStorage.getItem("adminToken") || "")
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const [tab, setTab] = useState("bookings")

    // Проверяем токен при загрузке
    useEffect(() => {
        if (token) {
            fetch("/api/admin/bookings", {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then((res) => {
                    if (res.ok) setIsLoggedIn(true)
                    else {
                        setToken("")
                        localStorage.removeItem("adminToken")
                    }
                })
                .catch(() => {
                    setToken("")
                    localStorage.removeItem("adminToken")
                })
        }
    }, [token])

    // ===== ФОРМА ВХОДА =====
    if (!isLoggedIn) {
        return (
            <LoginForm
                onLogin={(t) => {
                    setToken(t)
                    setIsLoggedIn(true)
                    localStorage.setItem("adminToken", t)
                }}
            />
        )
    }

    return (
        <main className="admin-page">
            <div className="admin-header">
                <h1 className="font-display">🌲 Админ-панель</h1>
                <button
                    onClick={() => {
                        setToken("")
                        setIsLoggedIn(false)
                        localStorage.removeItem("adminToken")
                    }}
                    className="admin-logout">
                    Выйти
                </button>
            </div>

            {/* Вкладки */}
            <div className="admin-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`admin-tab ${tab === t.key ? "admin-tab--active" : ""}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="admin-content">
                {tab === "bookings" && <BookingsTab token={token} />}
                {tab === "prices" && <PricesTab token={token} />}
                {tab === "calendar" && <CalendarTab token={token} />}
            </div>
        </main>
    )
}

// ===== ФОРМА ВХОДА =====
function LoginForm({ onLogin }) {
    const [login, setLogin] = useState("")
    const [password, setPassword] = useState("")
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)

    async function handleSubmit(e) {
        e.preventDefault()
        setError("")
        setLoading(true)

        try {
            const res = await fetch("/api/admin/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ login, password }),
            })

            const data = await res.json()

            if (res.ok) {
                onLogin(data.token)
            } else {
                setError(data.error || "Ошибка входа")
            }
        } catch (err) {
            setError("Ошибка соединения с сервером")
        } finally {
            setLoading(false)
        }
    }

    return (
        <main className="admin-login">
            <form onSubmit={handleSubmit} className="admin-login__form">
                <h2 className="font-display" style={{ color: "var(--forest)", marginBottom: 24 }}>
                    🌲 Вход в админку
                </h2>
                <input
                    type="text"
                    placeholder="Логин"
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    className="form-input"
                    style={{ marginBottom: 12 }}
                />
                <input
                    type="password"
                    placeholder="Пароль"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input"
                    style={{ marginBottom: 16 }}
                />
                {error && (
                    <div className="booking-error" style={{ marginBottom: 12 }}>
                        {error}
                    </div>
                )}
                <button type="submit" className="btn-primary" style={{ width: "100%" }} disabled={loading}>
                    {loading ? "Вход..." : "Войти"}
                </button>
            </form>
        </main>
    )
}

// ===== ВКЛАДКА: БРОНИРОВАНИЯ =====
function BookingsTab({ token }) {
    const [bookings, setBookings] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [manualEdit, setManualEdit] = useState(null)
    const [manualEditSaving, setManualEditSaving] = useState(false)
    const [bookingEdit, setBookingEdit] = useState(null)
    const [bookingEditSaving, setBookingEditSaving] = useState(false)

    useEffect(() => {
        loadBookings()
    }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

    async function loadBookings() {
        setLoading(true)
        setError("")
        try {
            const [bookingsRes, blockedDatesRes] = await Promise.all([
                fetch("/api/admin/bookings", {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                fetch("/api/admin/blocked-dates", {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ])

            if (!bookingsRes.ok || !blockedDatesRes.ok) throw new Error("Ошибка загрузки")

            const bookingsData = await bookingsRes.json()
            const blockedDatesData = await blockedDatesRes.json()
            const loadedBookings = Array.isArray(bookingsData) ? bookingsData : bookingsData.bookings || []
            const manualBlocks = groupManualBlockedDates(Array.isArray(blockedDatesData) ? blockedDatesData : [])
            const combinedItems = [
                ...loadedBookings.map((booking) => ({ ...booking, type: "booking" })),
                ...manualBlocks,
            ].sort((a, b) => getBookingSortTime(b) - getBookingSortTime(a))

            setBookings(combinedItems)
        } catch (err) {
            setError("Не удалось загрузить бронирования")
        } finally {
            setLoading(false)
        }
    }

    function getBookingSortTime(item) {
        const value = item.created_at || item.sort_date || item.check_in
        if (!value) return 0

        const dateStr = String(value)
        const parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
            ? new Date(`${dateStr}T12:00:00`)
            : new Date(dateStr)

        return isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime()
    }

    async function changeStatus(id, status) {
        if (status === "CANCELLED") {
            const confirmed = window.confirm(
                "Вы уверены, что хотите отменить это бронирование? Даты будут разблокированы.",
            )
            if (!confirmed) return
        }

        try {
            const res = await fetch(`/api/admin/bookings/${id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ status }),
            })
            if (!res.ok) {
                const data = await res.json()
                alert(data.error || "Ошибка обновления статуса")
                return
            }
            loadBookings()
        } catch (err) {
            alert("Ошибка соединения с сервером")
        }
    }

    async function deleteBooking(id) {
        const confirmed = window.confirm(
            "Вы уверены, что хотите удалить это бронирование из списка? Действие необратимо.",
        )
        if (!confirmed) return

        try {
            const res = await fetch(`/api/admin/bookings/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            if (!res.ok) {
                const data = await res.json()
                alert(data.error || "Ошибка удаления")
                return
            }
            loadBookings()
        } catch (err) {
            alert("Ошибка соединения с сервером")
        }
    }

    async function deleteManualBlock(block) {
        const confirmed = window.confirm("Разблокировать все даты этой ручной блокировки?")
        if (!confirmed) return

        try {
            if (block.manual_block_id) {
                const res = await fetch(`/api/admin/blocked-date-groups/${block.manual_block_id}`, {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                const data = await res.json()

                if (!res.ok || data.deleted === 0) {
                    alert(data.error || "Ошибка разблокировки")
                    return
                }

                loadBookings()
                return
            }

            for (const date of block.dates) {
                const res = await fetch(`/api/admin/blocked-dates/${date}`, {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })

                if (!res.ok) {
                    const data = await res.json()
                    alert(data.error || "Ошибка разблокировки")
                    return
                }
            }

            loadBookings()
        } catch (err) {
            alert("Ошибка соединения с сервером")
        }
    }

    function startManualEdit(block) {
        setBookingEdit(null)
        setManualEdit({
            id: block.id,
            dates: block.dates,
            guestName: block.guest_name === "Ручная блокировка" ? "" : block.guest_name || "",
            phone: block.phone || "",
            guestCount: block.guest_count || "",
            comment: block.comment || "",
        })
    }

    function updateManualEdit(key, value) {
        setManualEdit((prev) => ({ ...prev, [key]: value }))
    }

    function cancelManualEdit() {
        setManualEdit(null)
    }

    function startBookingEdit(booking) {
        setManualEdit(null)
        setBookingEdit({
            id: booking.id,
            guestName: booking.guest_name || "",
            phone: booking.phone || "",
            guestCount: booking.guest_count || "",
            comment: booking.comment || "",
        })
    }

    function updateBookingEdit(key, value) {
        setBookingEdit((prev) => ({ ...prev, [key]: value }))
    }

    function cancelBookingEdit() {
        setBookingEdit(null)
    }

    async function saveBookingEdit(e) {
        e.preventDefault()
        if (!bookingEdit) return

        const guestName = bookingEdit.guestName.trim()
        const guestCount = Number(bookingEdit.guestCount)
        const phoneDigits = String(bookingEdit.phone).replace(/\D/g, "")

        if (guestName.length < 2) {
            alert("Укажите имя")
            return
        }
        if (phoneDigits.length < 10) {
            alert("Укажите корректный номер телефона")
            return
        }
        if (!Number.isInteger(guestCount) || guestCount < 1) {
            alert("Укажите корректное количество гостей")
            return
        }

        setBookingEditSaving(true)
        try {
            const res = await fetch(`/api/admin/bookings/${bookingEdit.id}/details`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    guestName,
                    phone: bookingEdit.phone,
                    guestCount,
                    comment: bookingEdit.comment,
                }),
            })
            const data = await res.json()

            if (!res.ok) {
                alert(data.error || "Ошибка сохранения бронирования")
                return
            }

            setBookingEdit(null)
            loadBookings()
        } catch (err) {
            alert("Ошибка соединения с сервером")
        } finally {
            setBookingEditSaving(false)
        }
    }

    async function saveManualEdit(e) {
        e.preventDefault()
        if (!manualEdit) return

        const guestName = manualEdit.guestName.trim()
        const guestCount = Number(manualEdit.guestCount)
        const phoneDigits = String(manualEdit.phone).replace(/\D/g, "")

        if (guestName.length < 2) {
            alert("Укажите имя")
            return
        }
        if (!Number.isInteger(guestCount) || guestCount < 1) {
            alert("Укажите корректное количество человек")
            return
        }
        if (manualEdit.phone.trim() && phoneDigits.length < 10) {
            alert("Укажите корректный номер телефона")
            return
        }

        setManualEditSaving(true)
        try {
            const res = await fetch("/api/admin/blocked-dates/manual-details", {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    dates: manualEdit.dates,
                    guestName,
                    phone: manualEdit.phone,
                    guestCount,
                    comment: manualEdit.comment,
                }),
            })
            const data = await res.json()

            if (!res.ok) {
                alert(data.error || "Ошибка сохранения ручной блокировки")
                return
            }

            setManualEdit(null)
            loadBookings()
        } catch (err) {
            alert("Ошибка соединения с сервером")
        } finally {
            setManualEditSaving(false)
        }
    }

    // FIX: форматируем дату из строки "YYYY-MM-DD"
    function formatDate(d) {
        if (!d) return "—"
        const s = String(d)
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
            const parts = s.substring(0, 10).split("-")
            return `${parts[2]}.${parts[1]}.${parts[0]}`
        }
        return s
    }

    // Сколько часов прошло с момента создания
    function hoursSinceCreation(createdAt) {
        if (!createdAt) return 0
        const created = new Date(createdAt)
        return Math.round((Date.now() - created.getTime()) / (1000 * 60 * 60))
    }

    if (loading) return <p>Загрузка...</p>
    if (error) return <p style={{ color: "#dc2626", textAlign: "center", padding: 40 }}>{error}</p>

    if (bookings.length === 0) {
        return (
            <p style={{ color: "var(--muted)", textAlign: "center", padding: 40 }}>Бронирований пока нет</p>
        )
    }

    return (
        <div className="bookings-list">
            {bookings.map((b) => {
                const isManualBlock = b.type === "manual_block"
                const statusColor = STATUS_COLORS[b.status] || "var(--muted)"
                const isEditingManualBlock = isManualBlock && manualEdit?.id === b.id
                const isEditingBooking = !isManualBlock && bookingEdit?.id === b.id
                const dateLabel =
                    isManualBlock && b.check_in === b.check_out
                        ? formatDate(b.check_in)
                        : `${formatDate(b.check_in)} — ${formatDate(b.check_out)}`

                return (
                    <div
                        key={b.id}
                        className={`booking-item ${isManualBlock ? "booking-item--manual" : ""}`}>
                        <div className="booking-item__header">
                            <div>
                                <strong>{b.guest_name}</strong>
                                {b.phone && <span className="booking-item__phone">{b.phone}</span>}
                            </div>
                            <span
                                className="booking-item__status"
                                style={{
                                    background: statusColor + "20",
                                    color: statusColor,
                                }}>
                                {STATUS_LABELS[b.status] || b.status}
                            </span>
                        </div>

                        <div className="booking-item__details">
                            <span>📅 {dateLabel}</span>
                            {isManualBlock ? (
                                b.guest_count ? <span>👥 {b.guest_count} человек</span> : null
                            ) : (
                                <span>👥 {b.guest_count} гостей</span>
                            )}
                            {!isManualBlock && <span>💰 {Number(b.total_price).toLocaleString("ru")} ₽</span>}
                        </div>

                        {b.comment && <div className="booking-item__comment">💬 {b.comment}</div>}

                        {isEditingBooking && (
                            <form className="booking-edit-form" onSubmit={saveBookingEdit}>
                                <div className="booking-edit-form__row">
                                    <label className="form-label">
                                        Имя
                                        <input
                                            type="text"
                                            value={bookingEdit.guestName}
                                            onChange={(e) => updateBookingEdit("guestName", e.target.value)}
                                            className="form-input"
                                            placeholder="Имя гостя"
                                        />
                                    </label>
                                    <label className="form-label">
                                        Телефон
                                        <input
                                            type="tel"
                                            value={bookingEdit.phone}
                                            onChange={(e) => updateBookingEdit("phone", e.target.value)}
                                            className="form-input"
                                            placeholder="+7 (999) 123-45-67"
                                        />
                                    </label>
                                </div>
                                <div className="booking-edit-form__row">
                                    <label className="form-label">
                                        Количество гостей
                                        <input
                                            type="number"
                                            min="1"
                                            value={bookingEdit.guestCount}
                                            onChange={(e) => updateBookingEdit("guestCount", e.target.value)}
                                            className="form-input"
                                            placeholder="Например, 12"
                                        />
                                    </label>
                                    <label className="form-label">
                                        Комментарий
                                        <textarea
                                            value={bookingEdit.comment}
                                            onChange={(e) => updateBookingEdit("comment", e.target.value)}
                                            className="form-input form-textarea"
                                            rows={3}
                                            placeholder="Необязательно"
                                        />
                                    </label>
                                </div>
                                <div className="booking-edit-form__actions">
                                    <button type="submit" className="btn-small btn-small--green" disabled={bookingEditSaving}>
                                        {bookingEditSaving ? "Сохраняю..." : "Сохранить"}
                                    </button>
                                    <button type="button" className="btn-small" onClick={cancelBookingEdit}>
                                        Отмена
                                    </button>
                                </div>
                            </form>
                        )}

                        {isEditingManualBlock && (
                            <form className="manual-edit-form" onSubmit={saveManualEdit}>
                                <div className="manual-edit-form__row">
                                    <label className="form-label">
                                        Имя
                                        <input
                                            type="text"
                                            value={manualEdit.guestName}
                                            onChange={(e) => updateManualEdit("guestName", e.target.value)}
                                            className="form-input"
                                            placeholder="Имя гостя"
                                        />
                                    </label>
                                    <label className="form-label">
                                        Телефон
                                        <input
                                            type="tel"
                                            value={manualEdit.phone}
                                            onChange={(e) => updateManualEdit("phone", e.target.value)}
                                            className="form-input"
                                            placeholder="+7 (999) 123-45-67"
                                        />
                                    </label>
                                </div>
                                <div className="manual-edit-form__row">
                                    <label className="form-label">
                                        Количество человек
                                        <input
                                            type="number"
                                            min="1"
                                            value={manualEdit.guestCount}
                                            onChange={(e) => updateManualEdit("guestCount", e.target.value)}
                                            className="form-input"
                                            placeholder="Например, 12"
                                        />
                                    </label>
                                    <label className="form-label">
                                        Комментарий
                                        <textarea
                                            value={manualEdit.comment}
                                            onChange={(e) => updateManualEdit("comment", e.target.value)}
                                            className="form-input form-textarea"
                                            rows={3}
                                            placeholder="Необязательно"
                                        />
                                    </label>
                                </div>
                                <div className="manual-edit-form__actions">
                                    <button type="submit" className="btn-small btn-small--green" disabled={manualEditSaving}>
                                        {manualEditSaving ? "Сохраняю..." : "Сохранить"}
                                    </button>
                                    <button type="button" className="btn-small" onClick={cancelManualEdit}>
                                        Отмена
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Предупреждение для PENDING */}
                        {!isManualBlock && b.status === "PENDING" && (
                            <div className="booking-item__pending-warning">
                                ⏳ Ожидает оплаты ({hoursSinceCreation(b.created_at)} ч назад) — автоотмена через
                                24 ч
                            </div>
                        )}

                        <div className="booking-item__actions">
                            {isManualBlock ? (
                                <>
                                    {!isEditingManualBlock && (
                                        <button onClick={() => startManualEdit(b)} className="btn-small btn-small--green">
                                            Изменить
                                        </button>
                                    )}
                                    <button onClick={() => deleteManualBlock(b)} className="btn-small btn-small--red">
                                        ✕ Разблокировать даты
                                    </button>
                                </>
                            ) : (
                                <>
                                    {!isEditingBooking && (
                                        <button onClick={() => startBookingEdit(b)} className="btn-small btn-small--green">
                                            Изменить
                                        </button>
                                    )}
                                    {/* PENDING → PAID (подтвердить получение предоплаты) */}
                                    {b.status === "PENDING" && (
                                        <button
                                            onClick={() => changeStatus(b.id, "PAID")}
                                            className="btn-small btn-small--green">
                                            💳 Оплата получена
                                        </button>
                                    )}
                                    {/* PAID → CONFIRMED */}
                                    {b.status === "PAID" && (
                                        <button
                                            onClick={() => changeStatus(b.id, "CONFIRMED")}
                                            className="btn-small btn-small--green">
                                            ✓ Подтвердить
                                        </button>
                                    )}
                                    {/* Отмена (для любого не-отменённого) */}
                                    {b.status !== "CANCELLED" && (
                                        <button
                                            onClick={() => changeStatus(b.id, "CANCELLED")}
                                            className="btn-small btn-small--red">
                                            ✕ Отменить
                                        </button>
                                    )}
                                    {/* Удаление (только отменённые) */}
                                    {b.status === "CANCELLED" && (
                                        <button
                                            onClick={() => deleteBooking(b.id)}
                                            className="btn-small btn-small--red">
                                            🗑 Удалить из списка
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

// ===== ВКЛАДКА: ЦЕНЫ =====
function PricesTab({ token }) {
    const [settings, setSettings] = useState({
        weekday_price: 30000,
        weekend_price: 50000,
        guest_surcharge: 1000,
        included_guests: 15,
        max_guests: 30,
        deposit: 30000,
        cleaning_fee: 6000,
        pending_cancel_hours: 24,
    })
    const [saved, setSaved] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        fetch("/api/admin/settings", {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => res.json())
            .then((data) => setSettings(data))
            .catch(() => setError("Не удалось загрузить настройки"))
    }, [token])

    function handleChange(key, value) {
        setSettings({ ...settings, [key]: Number(value) })
        setSaved(false)
    }

    async function handleSave() {
        setError("")
        try {
            const res = await fetch("/api/admin/settings", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(settings),
            })
            if (!res.ok) {
                const data = await res.json()
                setError(data.error || "Ошибка сохранения")
                return
            }
            setSaved(true)
            setTimeout(() => setSaved(false), 3000)
        } catch (err) {
            setError("Ошибка соединения с сервером")
        }
    }

    const fields = [
        {
            key: "weekday_price",
            label: "Цена за будни (пн–чт) (₽)",
            hint: "Стоимость аренды за одну ночь в будни",
        },
        {
            key: "weekend_price",
            label: "Цена за выходные (пт–вс) (₽)",
            hint: "Стоимость аренды за одну ночь в выходные",
        },
        { key: "guest_surcharge", label: "Надбавка за гостя (₽)", hint: "За каждого гостя сверх включённых" },
        {
            key: "included_guests",
            label: "Гостей включено в цену",
            hint: "Сколько гостей входит в базовую цену",
        },
        { key: "max_guests", label: "Максимум гостей", hint: "Ограничение на количество гостей" },
        { key: "deposit", label: "Залог (₽)", hint: "Возвращается при отсутствии повреждений" },
        {
            key: "cleaning_fee",
            label: "Доплата за уборку (₽)",
            hint: "При сильном загрязнении после мероприятия",
        },
        {
            key: "pending_cancel_hours",
            label: "Автоотмена PENDING (часов)",
            hint: "Через сколько часов отменять неоплаченные брони",
        },
    ]

    return (
        <div className="prices-form">
            {fields.map((f) => (
                <div key={f.key} className="price-field">
                    <label className="form-label">{f.label}</label>
                    <input
                        type="number"
                        value={settings[f.key] || ""}
                        onChange={(e) => handleChange(f.key, e.target.value)}
                        className="form-input"
                        style={{ maxWidth: 200 }}
                    />
                    <span className="price-field__hint">{f.hint}</span>
                </div>
            ))}

            {error && (
                <div className="booking-error" style={{ marginTop: 12 }}>
                    {error}
                </div>
            )}

            <button onClick={handleSave} className="btn-primary" style={{ marginTop: 16 }}>
                Сохранить настройки
            </button>

            {saved && <span className="save-success">✅ Сохранено!</span>}
        </div>
    )
}

// ===== ВКЛАДКА: КАЛЕНДАРЬ =====
function CalendarTab({ token }) {
    const [month, setMonth] = useState(new Date())
    const [blockedDates, setBlockedDates] = useState([])
    const [manualBlock, setManualBlock] = useState({
        startDate: "",
        endDate: "",
        guestName: "",
        phone: "",
        guestCount: "",
        comment: "",
    })
    const [rangeAnchor, setRangeAnchor] = useState("")
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        loadDates()
    }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

    async function loadDates() {
        try {
            const res = await fetch("/api/admin/blocked-dates", {
                headers: { Authorization: `Bearer ${token}` },
            })
            if (!res.ok) throw new Error("Ошибка загрузки")
            const data = await res.json()
            setBlockedDates(data)
        } catch (err) {
            setError("Не удалось загрузить заблокированные даты")
        }
    }

    function isBlocked(day) {
        const dateStr = toLocalDateStr(month.getFullYear(), month.getMonth(), day)
        return blockedDates.some((d) => d.date === dateStr)
    }

    function getBlockedInfo(day) {
        const dateStr = toLocalDateStr(month.getFullYear(), month.getMonth(), day)
        return blockedDates.find((d) => d.date === dateStr)
    }

    function updateManualBlock(key, value) {
        setManualBlock((prev) => ({ ...prev, [key]: value }))
        if (key === "startDate" || key === "endDate") {
            setRangeAnchor("")
        }
        setError("")
    }

    function resetManualBlock() {
        setManualBlock({
            startDate: "",
            endDate: "",
            guestName: "",
            phone: "",
            guestCount: "",
            comment: "",
        })
        setRangeAnchor("")
    }

    function selectDateForManualBlock(dateStr) {
        setError("")

        if (!rangeAnchor) {
            setRangeAnchor(dateStr)
            setManualBlock((prev) => ({ ...prev, startDate: dateStr, endDate: dateStr }))
            return
        }

        const startDate = dateStr < rangeAnchor ? dateStr : rangeAnchor
        const endDate = dateStr < rangeAnchor ? rangeAnchor : dateStr
        setManualBlock((prev) => ({ ...prev, startDate, endDate }))
        setRangeAnchor("")
    }

    function isInManualSelection(day) {
        if (!manualBlock.startDate) return false
        const dateStr = toLocalDateStr(month.getFullYear(), month.getMonth(), day)
        const endDate = manualBlock.endDate || manualBlock.startDate
        const start = manualBlock.startDate < endDate ? manualBlock.startDate : endDate
        const end = manualBlock.startDate < endDate ? endDate : manualBlock.startDate
        return dateStr >= start && dateStr <= end
    }

    function getBlockedTitle(info) {
        if (!info) return "Свободно"

        const parts = [info.reason || "Заблокировано"]
        if (info.guest_name) parts.push(`Имя: ${info.guest_name}`)
        if (info.phone) parts.push(`Телефон: ${info.phone}`)
        if (info.guest_count) parts.push(`Человек: ${info.guest_count}`)
        if (info.comment) parts.push(`Комментарий: ${info.comment}`)
        return parts.join("\n")
    }

    async function toggleDate(day) {
        const dateStr = toLocalDateStr(month.getFullYear(), month.getMonth(), day)
        const info = getBlockedInfo(day)

        try {
            if (info) {
                if (info.booking_id) {
                    alert("Эта дата привязана к бронированию. Отмените бронирование, чтобы разблокировать.")
                    return
                }
                if (info.source === "avito") {
                    alert("Эта дата пришла из Авито. Снимите её в календаре Авито, чтобы разблокировать.")
                    return
                }

                const confirmed = window.confirm(`Разблокировать дату ${dateStr}?`)
                if (!confirmed) return

                const res = await fetch(`/api/admin/blocked-dates/${dateStr}`, {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                const data = await res.json()

                if (!res.ok || data.deleted === 0) {
                    setError(data.error || "Не удалось разблокировать дату")
                    return
                }
            } else {
                selectDateForManualBlock(dateStr)
                return
            }

            loadDates()
        } catch (err) {
            alert("Ошибка соединения с сервером")
        }
    }

    async function submitManualBlock(e) {
        e.preventDefault()
        setError("")

        const startDate = manualBlock.startDate
        const endDate = manualBlock.endDate || manualBlock.startDate
        const guestName = manualBlock.guestName.trim()
        const guestCount = Number(manualBlock.guestCount)
        const phoneDigits = String(manualBlock.phone).replace(/\D/g, "")

        if (!startDate) {
            setError("Выберите дату или диапазон")
            return
        }
        if (endDate < startDate) {
            setError("Дата окончания не может быть раньше даты начала")
            return
        }
        if (guestName.length < 2) {
            setError("Укажите имя")
            return
        }
        if (!Number.isInteger(guestCount) || guestCount < 1) {
            setError("Укажите корректное количество человек")
            return
        }
        if (manualBlock.phone.trim() && phoneDigits.length < 10) {
            setError("Укажите корректный номер телефона")
            return
        }

        setSaving(true)
        try {
            const res = await fetch("/api/admin/blocked-dates", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    startDate,
                    endDate,
                    guestName,
                    phone: manualBlock.phone,
                    guestCount,
                    comment: manualBlock.comment,
                }),
            })
            const data = await res.json()

            if (!res.ok) {
                setError(data.error || "Не удалось заблокировать даты")
                return
            }

            resetManualBlock()
            loadDates()
        } catch (err) {
            setError("Ошибка соединения с сервером")
        } finally {
            setSaving(false)
        }
    }

    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
    const firstDay = (() => {
        const d = new Date(month.getFullYear(), month.getMonth(), 1).getDay()
        return d === 0 ? 6 : d - 1
    })()

    return (
        <div>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 20 }}>
                Кликните по свободной дате, чтобы выбрать её для ручной блокировки. Для диапазона кликните
                первую и последнюю дату.
                <br />
                🔴 — бронь, 🟡 — ручная блокировка.
            </p>

            {error && (
                <div className="booking-error" style={{ marginBottom: 12 }}>
                    {error}
                </div>
            )}

            <form className="manual-block-form" onSubmit={submitManualBlock}>
                <div className="manual-block-form__row">
                    <label className="form-label">
                        С даты
                        <input
                            type="date"
                            value={manualBlock.startDate}
                            onChange={(e) => updateManualBlock("startDate", e.target.value)}
                            className="form-input"
                        />
                    </label>
                    <label className="form-label">
                        По дату
                        <input
                            type="date"
                            value={manualBlock.endDate}
                            onChange={(e) => updateManualBlock("endDate", e.target.value)}
                            className="form-input"
                        />
                    </label>
                </div>
                <div className="manual-block-form__row">
                    <label className="form-label">
                        Имя
                        <input
                            type="text"
                            value={manualBlock.guestName}
                            onChange={(e) => updateManualBlock("guestName", e.target.value)}
                            className="form-input"
                            placeholder="Имя гостя"
                        />
                    </label>
                    <label className="form-label">
                        Телефон
                        <input
                            type="tel"
                            value={manualBlock.phone}
                            onChange={(e) => updateManualBlock("phone", e.target.value)}
                            className="form-input"
                            placeholder="+7 (999) 123-45-67"
                        />
                    </label>
                </div>
                <div className="manual-block-form__row">
                    <label className="form-label">
                        Количество человек
                        <input
                            type="number"
                            min="1"
                            value={manualBlock.guestCount}
                            onChange={(e) => updateManualBlock("guestCount", e.target.value)}
                            className="form-input"
                            placeholder="Например, 12"
                        />
                    </label>
                    <label className="form-label">
                        Комментарий
                        <textarea
                            value={manualBlock.comment}
                            onChange={(e) => updateManualBlock("comment", e.target.value)}
                            className="form-input form-textarea"
                            rows={3}
                            placeholder="Необязательно"
                        />
                    </label>
                </div>
                <div className="manual-block-form__actions">
                    <button type="submit" className="btn-primary" disabled={saving}>
                        {saving ? "Блокирую..." : "Заблокировать"}
                    </button>
                    <button type="button" className="btn-outline" onClick={resetManualBlock}>
                        Очистить
                    </button>
                </div>
            </form>

            <div className="admin-calendar">
                <div className="calendar-header">
                    <button
                        onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1))}
                        className="calendar-nav">
                        ‹
                    </button>
                    <span className="calendar-month font-display">
                        {MONTHS[month.getMonth()]} {month.getFullYear()}
                    </span>
                    <button
                        onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1))}
                        className="calendar-nav">
                        ›
                    </button>
                </div>

                <div className="calendar-grid">
                    {DAYS.map((d) => (
                        <div key={d} className="calendar-dayname">
                            {d}
                        </div>
                    ))}
                    {Array.from({ length: firstDay }).map((_, i) => (
                        <div key={`e-${i}`} />
                    ))}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                        const day = i + 1
                        const blocked = isBlocked(day)
                        const info = getBlockedInfo(day)
                        const isBooking = info?.booking_id
                        const isToday = toLocalDateStr(month.getFullYear(), month.getMonth(), day) === TODAY
                        const isManualSelection = !blocked && isInManualSelection(day)

                        return (
                            <button
                                key={day}
                                onClick={() => toggleDate(day)}
                                className={`calendar-day admin-calendar-day
                  ${blocked ? (isBooking ? "admin-day--booked" : "admin-day--blocked") : ""}
                  ${isManualSelection ? "admin-day--selection" : ""}
                  ${isToday ? "calendar-day--today" : ""}`}
                                title={blocked ? getBlockedTitle(info) : "Свободно"}>
                                {day}
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default AdminPage
